import React, { useEffect, useState } from "react";
import { ReportSummaryItemApi } from "../api/Reports";
import {
  CreateArticleApi,
  Item,
  ItemWithMetaData,
  UpdateWebContentApi,
} from "../api/Items";
import { DropdownItem } from "../shared-components/DropdownCombo";
import { useCreateItemArticle, useUpdateItemArticle } from "../api/ItemsApi";
import { validURL } from "../utils";
import PageHeader from "../shared-components/PageHeader";

import InputField from "../shared-components/InputField";
import TextAreaField from "../shared-components/TextAreaField";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";

import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";
import PersonFormStringDate from "../persons/PersonFormStringDate";

export interface WebContentModalState {
  report?: ReportSummaryItemApi;
  profileUUID?: string;
  webContentItem?: ItemWithMetaData;
}

const MAX_DESCRIPTION_LIMIT = 2048;

type Props = {
  showModal: boolean;
  close: (item?: Item) => void;
  initialState?: WebContentModalState;
  behaviors?: string[];
  isNew?: boolean;
  keywordUUID?: string;
  showDateError?: boolean;
};

const ModalWebContentForm = ({
  showModal,
  close,
  initialState,
  behaviors,
  isNew,
  showDateError = false,
}: Props) => {
  return (
    <Modal open={showModal} onClose={close} width={"w-full max-w-2xl p-3"}>
      <WebContentForm
        report={initialState?.report}
        profileUUID={initialState?.profileUUID || ""}
        behaviors={behaviors}
        onClose={close}
        webContent={initialState?.webContentItem}
        isNew={isNew}
        showDateError={showDateError}
      />
    </Modal>
  );
};

type AddWebContentProps = {
  report?: ReportSummaryItemApi;
  profileUUID: string;
  behaviors?: string[];
  onClose: (item?: Item) => void;
  webContent?: ItemWithMetaData;
  isNew?: boolean;
  showDateError?: boolean;
};

type formErrors = {
  title: boolean;
  url: boolean;
  imageUrl: boolean;
  type: boolean;
  text: boolean;
  ratings: boolean;
  date: boolean;
  submissionTried: boolean;
};

function webContentItemToArticleApi(
  report: ReportSummaryItemApi | undefined,
  profileUUID: string,
  webContent: ItemWithMetaData | undefined
): CreateArticleApi {
  const clonedContent = Object.assign({}, webContent?.item.content);

  return {
    report_uuid: report?.report_uuid ?? "",
    profile_uuid: profileUUID,
    url: clonedContent.url ?? "",
    title: clonedContent.title ?? "",
    text: clonedContent.text ?? "",
    score: clonedContent.score ?? 0,
    action: "review",
    status: "TO_DO",
    created_at: clonedContent?.created_at?.split("T")?.[0] ?? null,
    ratings: [...(webContent?.rating ?? [])],
  };
}

const WebContentForm = ({
  report,
  behaviors,
  onClose,
  webContent,
  profileUUID,
  isNew,
  showDateError = false,
}: AddWebContentProps) => {
  const isEdit = !!webContent;

  const [behaviourOptions, setBehaviourOptions] = useState<DropdownItem[]>([]);

  const [post, setPost] = useState<CreateArticleApi>(
    webContentItemToArticleApi(report, profileUUID, webContent)
  );

  const [errors, setErrors] = useState<formErrors>({
    title: false,
    url: false,
    imageUrl: false,
    type: false,
    text: false,
    ratings: false,
    date: false,
    submissionTried: false,
  });

  const {
    mutate: createArticle,
    isLoading: isLoadingArticleCreating,
    error: articleCreationError,
    isSuccess: successArticle,
    data: createdArticleUUID,
    reset: resetCreateArticle,
  } = useCreateItemArticle();

  const {
    mutate: updateArticle,
    isLoading: isLoadingArticleUpdate,
    error: updateCreationError,
    isSuccess: successUpdateArticle,
    data: updatedArticle,
    reset: resetUpdateArticle,
  } = useUpdateItemArticle();

  const { behaviorMappingActions } = useFamaFlags();

  useEffect(() => {
    if (successArticle && createdArticleUUID) {
      onClose(createdArticleUUID);
      resetCreateArticle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successArticle, onClose]);

  useEffect(() => {
    if (successUpdateArticle && updatedArticle) {
      onClose(updatedArticle);
      resetUpdateArticle();

      if (webContent) {
        webContent.item.content = { ...updatedArticle.content };
        webContent.rating = post.ratings.map((p) => ({
          external_id: "",
          rater_uuid: "",
          name: p.name,
          score_type: p.score_type,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdateArticle, onClose]);

  useEffect(() => {
    if (behaviors) {
      const newBehaviors: string[] = [];
      behaviors.forEach((behavior) => {
        if (!newBehaviors.includes(behavior)) {
          newBehaviors.push(behavior);
        }
      });

      const newBehaviorOptions = newBehaviors
        .map((value, index) => {
          return { id: index.toString(), label: value };
        })
        .sort((itemA, itemB) => itemA.label.localeCompare(itemB.label));

      setBehaviourOptions(newBehaviorOptions);
    }
  }, [behaviors]);

  useEffect(() => {
    // Form Validation
    const titleError = post.title.length === 0;
    const profileError =
      post.url.length === 0 || post.profile_uuid.length === 0;
    const textError = post.text.length === 0;
    const urlError = !validURL(post.url);
    const dateError = !post.created_at;

    setErrors((errors) => ({
      ...errors,
      title: titleError,
      profile: profileError,
      url: urlError,
      text: textError,
      date: dateError,
    }));
  }, [setErrors, post]);

  useEffect(() => {
    if (showDateError) {
      setErrors((prev) => ({
        ...prev,
        date: true,
        submissionTried: true,
      }));
    }
  }, [showDateError]);

  const onRatingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRating = e.target.name;

    // If checked is removed, remove from Set
    if (!e.target.checked) {
      const newRatings = post.ratings.filter((rating) => {
        return rating.name !== newRating;
      });
      setPost({ ...post, ratings: newRatings });
    } else {
      const newRatings = post.ratings;
      newRatings.push({ name: newRating, score_type: "behavior" });
      setPost({ ...post, ratings: newRatings });
    }
  };

  const handleSavePost = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setErrors({ ...errors, submissionTried: true });

    if (
      !errors.url &&
      !errors.title &&
      !errors.imageUrl &&
      !errors.text &&
      !errors.ratings &&
      !errors.type &&
      !errors.date
    ) {
      if (!isEdit) createArticle(post);
      if (webContent) {
        const updatedContent = Object.assign(webContent.item.content);
        updatedContent.url = post.url;
        updatedContent.title = post.title.substring(0, 255);
        updatedContent.text = post.text.substring(0, MAX_DESCRIPTION_LIMIT);
        if (post.created_at) {
          updatedContent.created_at = post.created_at;
        }
        const updateInput: UpdateWebContentApi = {
          item_uuid: webContent.item.external_id,
          content: updatedContent,
          ratings: post.ratings.map((rating) => {
            return {
              name: rating.name,
              score_type: rating.score_type,
            };
          }),
        };

        updateArticle(updateInput);
      }
    }
  };

  const showError = (): boolean => {
    const formError =
      errors.title ||
      errors.url ||
      errors.imageUrl ||
      errors.ratings ||
      errors.text ||
      errors.type ||
      errors.date;
    return (
      errors.submissionTried &&
      (formError || !!articleCreationError || !!updateCreationError)
    );
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  function isBehaviorChecked(label: string): boolean {
    return !!post.ratings.find((rating) => rating.name === label);
  }

  return (
    <>
      <PageHeader title={isEdit ? "Edit Content" : "Add New Content"} />
      <form>
        <div className="flex justify-between">
          <InputField
            classes="w-4/5 pr-2"
            className="w-full"
            name={"content-type"}
            placeholder="Web Content"
            label={"Content Type"}
            labelClassName="font-bold"
            value={"Web Content"}
            readOnly={true}
          />
          <PersonFormStringDate
            _name="date"
            _label="Date *"
            index={0}
            prefill={post.created_at}
            onChange={(dateValue) => {
              setPost({
                ...post,
                created_at: dateValue,
              });
            }}
            classes="w-36"
          />
        </div>

        <div className="flex justify-between">
          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setPost({ ...post, title: e.target.value });
            }}
            name={"title"}
            placeholder="Type here..."
            label={"Content Title *"}
            labelClassName="font-bold"
            value={post.title}
          />
        </div>

        <div className="flex justify-between">
          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setPost({ ...post, url: e.target.value });
            }}
            name={"link"}
            placeholder="Insert Url Address here..."
            label={"Content Link *"}
            labelClassName="font-bold"
            value={post.url}
          />
        </div>

        <div title="Description" className="">
          <TextAreaField
            classes="w-full"
            handleChange={(e) => {
              setPost({ ...post, text: e.target.value });
            }}
            name={"description"}
            placeholder="Type here..."
            label={"Description *"}
            type="text"
            className="h-28 w-full"
            labelClassName="font-bold"
            value={post.text}
          />
          {post.text.length > MAX_DESCRIPTION_LIMIT && (
            <div className="bg-red-200 text-red-400 text-sm p-4">
              Your description is longer than {MAX_DESCRIPTION_LIMIT} characters
              and it will be truncated after saving.
            </div>
          )}
        </div>

        <div title="Flagged Content" className="">
          <div className="grid grid-cols-3">
            {behaviourOptions.map((behaviour) => (
              <LabeledCheckbox
                name={behaviour.label}
                label={behaviorMap(
                  behaviour.label,
                  behaviorMappingActions,
                  "key"
                )}
                classes="m-2"
                onChange={onRatingChange}
                key={behaviour.id}
                checked={isBehaviorChecked(behaviour.label)}
              ></LabeledCheckbox>
            ))}
          </div>
        </div>

        {showError() && (
          <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
            <ul>
              {errors.title && <li>Title is a required field.</li>}
              {errors.url && (
                <li>
                  Content Link is a required field and should have valid URL.
                </li>
              )}
              {errors.text && <li>Description is a required field.</li>}
              {errors.date && <li>Date is a required field.</li>}
              {errors.imageUrl && <li>ImageURL is not a valid URL.</li>}
              {errors.ratings && (
                <li>At least one behavior should be selected.</li>
              )}
              {!successArticle &&
                (!!articleCreationError || !!updateCreationError) && (
                  <li>
                    Error updating article, please try again or contact support.
                  </li>
                )}
            </ul>
          </div>
        )}
      </form>

      <div className="w-full border-t border-gray-300"></div>

      <div className="flex items-center justify-end pt-4 gap-3">
        <form>
          <Button
            type={ButtonTypes.default}
            text="Cancel"
            colorType={ButtonColorTypes.cancel}
            onClick={handleClose}
          />
        </form>
        <form className="flex-2">
          {isLoadingArticleCreating || isLoadingArticleUpdate ? (
            <LoadingSpinner />
          ) : (
            <Button
              text={isNew ? "Save & Add" : "Save"}
              onClick={handleSavePost}
              colorType={ButtonColorTypes.success}
            />
          )}
        </form>
      </div>
    </>
  );
};

export default ModalWebContentForm;
